/**
 * Risk level enumeration with associated metadata
 */
export enum RiskLevel {
  CRITICAL = "CRITICAL",
  HIGH = "HIGH",
  MODERATE = "MODERATE",
  LOW = "LOW",
  UNKNOWN = "UNKNOWN",
}

/**
 * Risk level metadata including thresholds and display colors
 */
interface RiskLevelConfig {
  level: RiskLevel;
  minScore: number | null; // null for UNKNOWN (no score)
  color: string; // Tailwind CSS color class
  textColor: string; // Tailwind CSS text color class
}

/**
 * RiskScale class manages risk level determination and presentation
 */
export class RiskScale {
  /**
   * Risk level configurations.
   * IMPORTANT: Must be ordered from highest to lowest minScore for getRiskLevelFromScore() to work correctly.
   */
  private static readonly configs: RiskLevelConfig[] = [
    {
      level: RiskLevel.CRITICAL,
      minScore: 85,
      color: "bg-red-600",
      textColor: "text-white",
    },
    {
      level: RiskLevel.HIGH,
      minScore: 65,
      color: "bg-orange-500",
      textColor: "text-white",
    },
    {
      level: RiskLevel.MODERATE,
      minScore: 40,
      color: "bg-amber-400",
      textColor: "text-gray-900",
    },
    {
      level: RiskLevel.LOW,
      minScore: 0,
      color: "bg-emerald-500",
      textColor: "text-white",
    },
    {
      level: RiskLevel.UNKNOWN,
      minScore: null,
      color: "bg-gray-500",
      textColor: "text-white",
    },
  ];

  /**
   * Get risk level from numeric score
   * @param score - The numeric risk score (0-100), or null/undefined/NaN for unknown
   * @returns The corresponding RiskLevel
   */
  static getRiskLevelFromScore(score: number | null | undefined): RiskLevel {
    // Handle invalid or missing scores
    if (score === null || score === undefined || Number.isNaN(score)) {
      return RiskLevel.UNKNOWN;
    }

    // Find the highest tier that the score qualifies for
    for (const config of this.configs) {
      if (config.minScore !== null && score >= config.minScore) {
        return config.level;
      }
    }

    // Fallback to LOW if score is below all thresholds
    return RiskLevel.LOW;
  }

  /**
   * Get color classes for a given risk level
   * @param level - The risk level
   * @returns Object containing background and text color classes
   */
  static getColorClasses(level: RiskLevel): {
    color: string;
    textColor: string;
  } {
    const config = this.configs.find((c) => c.level === level);
    if (!config) {
      // Fallback to UNKNOWN styling
      const unknownConfig = this.configs.find(
        (c) => c.level === RiskLevel.UNKNOWN
      );
      return {
        color: unknownConfig?.color ?? "bg-gray-500",
        textColor: unknownConfig?.textColor ?? "text-white",
      };
    }
    return {
      color: config.color,
      textColor: config.textColor,
    };
  }

  /**
   * Get minimum score for a risk level
   * @param level - The risk level
   * @returns The minimum score, or null for UNKNOWN
   */
  static getMinScore(level: RiskLevel): number | null {
    const config = this.configs.find((c) => c.level === level);
    return config?.minScore ?? null;
  }

  /**
   * Get all risk level configs (useful for display/documentation)
   * @returns Array of all risk level configurations
   */
  static getAllConfigs(): readonly RiskLevelConfig[] {
    return this.configs;
  }
}
